import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  subscriptionService,
  categoryService,
  analyticsService
} from '../services';
import { recordSavingsEvent } from '../services/savingsTrackerService';
import { supabase } from '../services/supabaseClient';

const SubscriptionContext = createContext();

export const useSubscriptions = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptions must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user from Supabase directly (avoid circular dependency with AuthContext)
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    getUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // State
  const [subscriptions, setSubscriptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingsUpdated, setSavingsUpdated] = useState(0); // Trigger for savings updates

  // ========== Date Formatting Helpers ==========

  // Format next billing date as "Nov 11"
  const formatNextBillingDate = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString + 'T00:00:00'); // Force local timezone
      if (isNaN(date.getTime())) return '';

      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();

      return `${month} ${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Calculate next billing date with rollover logic
  const calculateNextBillingDate = (lastBillDate, billingCycle) => {
    if (!lastBillDate) return null;

    try {
      const [year, month, day] = lastBillDate.split('-').map(Number);

      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null;
      }

      const lastDate = new Date(year, month - 1, day);

      if (isNaN(lastDate.getTime())) {
        return null;
      }

      let nextDate = new Date(lastDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const addBillingCycle = (date) => {
        const tempDate = new Date(date);
        const originalDay = lastDate.getDate();

        switch (billingCycle) {
          case 'weekly':
            tempDate.setDate(tempDate.getDate() + 7);
            break;
          case 'monthly':
            tempDate.setMonth(tempDate.getMonth() + 1);
            if (tempDate.getDate() < originalDay) {
              tempDate.setDate(0);
            }
            break;
          case 'quarterly':
            tempDate.setMonth(tempDate.getMonth() + 3);
            if (tempDate.getDate() < originalDay) {
              tempDate.setDate(0);
            }
            break;
          case 'yearly':
            tempDate.setFullYear(tempDate.getFullYear() + 1);
            if (tempDate.getDate() < originalDay) {
              tempDate.setDate(0);
            }
            break;
          default:
            tempDate.setMonth(tempDate.getMonth() + 1);
            if (tempDate.getDate() < originalDay) {
              tempDate.setDate(0);
            }
        }

        return tempDate;
      };

      nextDate = addBillingCycle(lastDate);

      let iterations = 0;
      const MAX_ITERATIONS = 100;

      while (nextDate < today && iterations < MAX_ITERATIONS) {
        nextDate = addBillingCycle(nextDate);
        iterations++;
      }

      if (iterations >= MAX_ITERATIONS) {
        return null;
      }

      return nextDate.toISOString().split('T')[0];
    } catch (error) {
      return null;
    }
  };

  // Get display renewal date for subscription
  const getDisplayRenewal = (subscription) => {
    try {
      // Only show next billing date for active and paused subscriptions
      if (subscription.status !== 'active' && subscription.status !== 'paused') {
        return '';
      }

      // If we have lastBillDate and billingCycle, recalculate
      if (subscription.lastBillDate && subscription.billingCycle) {
        const nextDate = calculateNextBillingDate(subscription.lastBillDate, subscription.billingCycle);
        if (nextDate) {
          return formatNextBillingDate(nextDate);
        }
      }

      // Fallback to existing renewal field if available - reformat if needed
      if (subscription.renewal && subscription.renewal !== 'Not set') {
        // If it has a comma and year, strip them to get just "Aug 14" format
        if (subscription.renewal.includes(',')) {
          const parts = subscription.renewal.split(',')[0]; // Get "Aug 14" from "Aug 14, 25"
          return parts.trim();
        }

        // Already in correct format "Jun 30" - return as-is
        return subscription.renewal;
      }

      return '';
    } catch (error) {
      return '';
    }
  };

  // Fetch initial data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subsData, catsData] = await Promise.all([
        subscriptionService.getAllSubscriptions(),
        categoryService.getAllCategories()
      ]);

      // Format renewal dates for all subscriptions
      const formattedSubs = (subsData || []).map(sub => {
        const formattedRenewal = getDisplayRenewal(sub);
        const nextBillingDate = sub.lastBillDate && sub.billingCycle
          ? calculateNextBillingDate(sub.lastBillDate, sub.billingCycle)
          : null;

        return {
          ...sub,
          renewal: formattedRenewal,
          nextBillingDate
        };
      });

      setSubscriptions(formattedSubs);
      setCategories(catsData?.map(cat => cat.name) || []);
    } catch (err) {
      console.error('SubscriptionContext Error:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint,
        full: err
      });
      setError(err.message || 'Failed to fetch data');
      // Set empty arrays on error to prevent crashes
      setSubscriptions([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Utility functions for subscription management
  const addSubscription = async (subscription) => {
    try {
      // Optimistic update - add to UI immediately with temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticSub = { ...subscription, id: tempId };
      setSubscriptions(prev => [optimisticSub, ...prev]);

      // Then create in database
      const newSub = await subscriptionService.createSubscription(subscription);

      // Replace temp subscription with real one from DB
      setSubscriptions(prev =>
        prev.map(sub => sub.id === tempId ? newSub : sub)
      );

      return newSub;
    } catch (err) {
      console.error('Error adding subscription:', err);
      // Remove optimistic entry on error
      setSubscriptions(prev => prev.filter(sub => !sub.id.toString().startsWith('temp-')));
      throw err;
    }
  };

  const updateSubscription = async (id, updatedSubscription) => {
    try {
      // Get original subscription for savings tracking
      const originalSub = subscriptions.find(sub => sub.id === id);

      // Optimistic update - update UI immediately
      setSubscriptions(prev =>
        prev.map(sub => sub.id === id ? { ...sub, ...updatedSubscription } : sub)
      );

      // Then update database in background
      const updated = await subscriptionService.updateSubscription(id, updatedSubscription);

      // Update again with actual DB response to ensure consistency
      setSubscriptions(prev =>
        prev.map(sub => sub.id === id ? updated : sub)
      );

      // Track savings events if applicable (wrapped in try-catch to not break main flow)
      if (currentUser?.id && originalSub) {
        try {
          // Check if subscription was canceled
          if (originalSub.status !== 'cancelled' && updatedSubscription.status === 'cancelled') {
            await recordSavingsEvent(
              currentUser.id,
              id,
              originalSub.name,
              'canceled',
              parseFloat(originalSub.cost || originalSub.price),
              0,
              'Subscription canceled by user'
            );

            // Trigger savings update for components listening
            setSavingsUpdated(prev => prev + 1);
          }

          // Check if price was reduced (downgrade) - check both cost and price fields
          const oldPrice = parseFloat(originalSub.cost || originalSub.price || 0);
          const newPrice = parseFloat(updatedSubscription.cost || updatedSubscription.price || 0);

          if (oldPrice > 0 && newPrice > 0 && newPrice < oldPrice) {
            await recordSavingsEvent(
              currentUser.id,
              id,
              originalSub.name,
              'downgraded',
              oldPrice,
              newPrice,
              'Subscription price reduced'
            );

            // Trigger savings update
            setSavingsUpdated(prev => prev + 1);
          }

          // Check if switched to annual billing (check both billingCycle and billing_cycle)
          const oldCycle = originalSub.billingCycle || originalSub.billing_cycle;
          const newCycle = updatedSubscription.billingCycle || updatedSubscription.billing_cycle;

          if (oldCycle && newCycle) {
            const wasMonthly = oldCycle === 'monthly';
            const nowAnnual = newCycle === 'yearly' || newCycle === 'annual';

            if (wasMonthly && nowAnnual) {
              const monthlyPrice = parseFloat(originalSub.cost || originalSub.price || 0);
              const annualPrice = parseFloat(updatedSubscription.cost || updatedSubscription.price || 0);
              const oldAnnualCost = monthlyPrice * 12; // What you would pay monthly

              if (monthlyPrice > 0 && annualPrice > 0 && annualPrice < oldAnnualCost) {
                await recordSavingsEvent(
                  currentUser.id,
                  id,
                  originalSub.name,
                  'annual_switch',
                  oldAnnualCost,
                  annualPrice,
                  'Switched to annual billing'
                );

                // Trigger savings update
                setSavingsUpdated(prev => prev + 1);
              }
            }
          }
        } catch (savingsError) {
          // Log but don't throw - savings tracking is optional
          console.error('⚠️  Savings tracking failed:', savingsError);
          console.error('   Error message:', savingsError.message);
          console.error('   Error code:', savingsError.code);
          console.error('   Error details:', savingsError.details);
          console.error('   Error hint:', savingsError.hint);
        }
      }

      return updated;
    } catch (err) {
      console.error('Error updating subscription:', err);
      // Rollback on error - refetch all subscriptions
      const { data } = await subscriptionService.getAllSubscriptions();
      setSubscriptions(data || []);
      throw err;
    }
  };

  const logSubscriptionEvent = async (subscriptionId, eventType, date, price, currency) => {
    try {
      return await subscriptionService.logSubscriptionEvent(subscriptionId, eventType, date, price, currency);
    } catch (err) {
      console.error('Error logging manual subscription event:', err);
      // We don't throw here to avoid breaking the UI for a non-critical background insert
    }
  };

  const deleteSubscription = async (id) => {
    try {
      // Get subscription info before deleting for savings tracking
      const deletedSub = subscriptions.find(sub => sub.id === id);

      // Delete from database
      await subscriptionService.deleteSubscription(id);

      // Record savings event if subscription was active (wrapped in try-catch to not break main flow)
      if (currentUser?.id && deletedSub && deletedSub.status === 'active') {
        try {
          await recordSavingsEvent(
            currentUser.id,
            id,
            deletedSub.name,
            'canceled',
            parseFloat(deletedSub.cost || deletedSub.price),
            0,
            'Subscription deleted by user'
          );

          // Trigger savings update
          setSavingsUpdated(prev => prev + 1);
        } catch (savingsError) {
          // Log but don't throw - savings tracking is optional
          console.error('⚠️  Savings tracking failed:', savingsError);
          console.error('   Error message:', savingsError.message);
          console.error('   Error code:', savingsError.code);
          console.error('   Error details:', savingsError.details);
          console.error('   Error hint:', savingsError.hint);
        }
      }

      // Remove from UI
      setSubscriptions(prev => prev.filter(sub => sub.id !== id));
    } catch (err) {
      console.error('Error deleting subscription:', err);
      throw err;
    }
  };

  const refreshSubscriptions = async () => {
    try {
      const data = await subscriptionService.getAllSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Error refreshing subscriptions:', err);
      throw err;
    }
  };

  // Computed values
  const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
  const pausedSubscriptions = subscriptions.filter(sub => sub.status === 'paused');
  const cancelledSubscriptions = subscriptions.filter(sub => sub.status === 'cancelled');

  // Calculate total monthly spend (normalize all billing cycles to monthly)
  const totalMonthlySpend = activeSubscriptions.reduce((sum, sub) => {
    const price = parseFloat(sub.price);
    let monthlyPrice = price;

    switch (sub.billingCycle) {
      case 'weekly':
        monthlyPrice = price * 4.33;
        break;
      case 'monthly':
        monthlyPrice = price;
        break;
      case 'quarterly':
        monthlyPrice = price / 3;
        break;
      case 'yearly':
        monthlyPrice = price / 12;
        break;
      default:
        monthlyPrice = price;
    }

    return sum + monthlyPrice;
  }, 0);

  const totalYearlySpend = totalMonthlySpend * 12;

  // Get upcoming renewals (next 30 days) - sorted by renewal date
  const upcomingRenewals = subscriptions
    .filter(sub => sub.status === 'active' && sub.renewal && sub.renewal !== '')
    .sort((a, b) => {
      if (a.nextBillingDate && b.nextBillingDate) {
        return new Date(a.nextBillingDate) - new Date(b.nextBillingDate);
      }
      return 0;
    })
    .slice(0, 5);

  const value = {
    // Data
    subscriptions,
    activeSubscriptions,
    pausedSubscriptions,
    cancelledSubscriptions,
    upcomingRenewals,
    categories,
    user: currentUser,
    savingsUpdated, // Trigger for components to refresh savings

    // Computed values
    totalMonthlySpend: parseFloat(totalMonthlySpend.toFixed(2)),
    totalYearlySpend: parseFloat(totalYearlySpend.toFixed(2)),
    totalSubscriptions: subscriptions.length,
    activeCount: activeSubscriptions.length,
    pausedCount: pausedSubscriptions.length,
    cancelledCount: cancelledSubscriptions.length,
    averageCost: activeSubscriptions.length > 0
      ? parseFloat((totalMonthlySpend / activeSubscriptions.length).toFixed(2))
      : 0,

    // State
    loading,
    error,

    // Actions
    addSubscription,
    updateSubscription,
    deleteSubscription,
    refreshSubscriptions,
    setSubscriptions,
    logSubscriptionEvent
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
